/**
 * Hook React para integrar el sistema de idle global
 */

import { useEffect, useRef } from 'react';
import { startIdleGlobal, StopHandle } from '@/lib/idle';
import { useAuthService } from '@/auth/useAuthService';

export const useIdleGlobal = () => {
  const stopHandleRef = useRef<StopHandle | null>(null);
  const initializationAttemptedRef = useRef(false);
  const { deleteTokens } = useAuthService();

  // Crear una referencia estable para la función de logout
  const logoutRef = useRef(deleteTokens);
  logoutRef.current = deleteTokens;

  useEffect(() => {
    const initIdleSystem = async () => {
      try {
        // Esperar a que el documento esté completamente cargado
        if (document.readyState !== 'complete') {
          await new Promise<void>((resolve) => {
            const handleLoad = () => {
              window.removeEventListener('load', handleLoad);
              resolve();
            };
            window.addEventListener('load', handleLoad);
          });
        }

        // Pequeño delay adicional para asegurar que los tokens estén disponibles
        await new Promise(resolve => setTimeout(resolve, 100));

        // Solo iniciar si hay tokens (usuario autenticado)
        const tokens = localStorage.getItem('tokens');

        if (!tokens) {

          // Solo configurar el listener una vez
          if (!initializationAttemptedRef.current) {
            initializationAttemptedRef.current = true;
            setupTokenListener();
          }
          return;
        }

        const stopHandle = await startIdleGlobal({
          logout: async () => {
            // Usar la función existente de logout a través de la ref
            await logoutRef.current();
          },
        });

        stopHandleRef.current = stopHandle;
      } catch (error) {
        console.error('Error initializing idle system', { error }, 'Hook');
      }
    };

    // Función para configurar listener de tokens
    const setupTokenListener = () => {

      const handleStorageChange = (event: StorageEvent) => {
        // Solo nos interesa cuando se agregan tokens
        if (event.key === 'tokens' && event.newValue && !event.oldValue) {
          // Remover el listener ya que ya no lo necesitamos
          window.removeEventListener('storage', handleStorageChange);

          // Pequeño delay para asegurar que los tokens estén completamente guardados
          setTimeout(() => {
            initIdleSystem();
          }, 200);
        }
      };

      window.addEventListener('storage', handleStorageChange);

      // También verificar periódicamente por si el evento storage no se dispara
      // (puede pasar si el cambio es en la misma pestaña)
      const pollInterval = setInterval(() => {
        const tokens = localStorage.getItem('tokens');
        if (tokens) {
          clearInterval(pollInterval);
          window.removeEventListener('storage', handleStorageChange);
          initIdleSystem();
        }
      }, 500); // Verificar cada 500ms

      // Limpiar el polling después de 30 segundos para evitar memory leaks
      setTimeout(() => {
        clearInterval(pollInterval);
        window.removeEventListener('storage', handleStorageChange);
      }, 30000);
    };

    initIdleSystem();

    // Cleanup al desmontar
    return () => {
      if (stopHandleRef.current) {
        stopHandleRef.current.stop();
        stopHandleRef.current = null;
      }
    };
  }, []); // 👈 Sin dependencias para evitar re-ejecuciones

  return {
    // Podríamos exponer métodos adicionales aquí si es necesario
  };
};
